import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import useSafeLocation from '../hooks/useSafeLocation.mjs';
import { fetchBulkReactions } from '../slices/reactionsSlice.mjs';
import { fetchBulkRatingAggregates } from '../slices/ratingsSlice.mjs';
import { setPageTitle, setPageDescription, setPageMetadata, setCanonicalUrl, resetPageMetadata } from '../slices/pageMetadataSlice.mjs';
import axios from 'axios';

import HomePageContent from './body/Home.mjs';
import UnifiedArticleDetailPage from './UnifiedArticleDetailPage.mjs';
import { setHeaderData } from '../slices/headerSlice.mjs';
import { setCurrentTopic } from '../slices/currentTopicSlice.mjs';
import useIsMounted from '../hooks/useIsMounted.mjs';
import { useArticleScope, buildScopeFromParams } from '../hooks/useArticleScope.mjs';
import { setPagination, setCurrentArticle } from '../slices/unifiedArticlesSlice.mjs';
import useSafeSearchParams from '../hooks/useSafeSearchParams.mjs';
import Breadcrumbs from '../components/navigation/Breadcrumbs.mjs';

/**
 * Topic Landing Page - displays StreamlinedSourceArticle content filtered by topic.
 * Uses the unified articles slice with scope-based caching.
 * 
 * Supports hierarchical URLs at any depth via splat route:
 * - /:categorySlug/* (e.g., /sports/ncaa-basketball, /sports/ncaa-basketball/duke)
 * 
 * Also handles hierarchical article URLs:
 * - /:categorySlug/:topicSlug/:articleSlug (detected when topic fetch fails)
 */
const TopicPage = () => {
    const dispatch = useDispatch();
    const location = useSafeLocation();
    const { categorySlug, '*': topicPath } = useParams();
    // React Router v7 returns splat as a string, split into segments
    const topicSegments = (topicPath || '').split('/').filter(Boolean);
    const topicSlug = topicSegments[topicSegments.length - 1];

    // Link state hint: when navigating from an article <Link state={{ type: 'article' }}>,
    // skip the topic fetch entirely — we know this is an article, not a topic.
    const isArticleHint = location.state?.type === 'article';
    const isMounted = useIsMounted();
    const [searchParams, setSearchParams] = useSafeSearchParams();

    const currentArticle = useSelector((state) => state.unifiedArticles?.currentArticle);
    const isSSRArticlePage = !!(currentArticle && currentArticle.slug === topicSlug);

    // DB-sourced category list for label lookups (persists across navigation)
    const pureCategories = useSelector((state) => state.headerSlice?.pureCategories || []);

    // Pre-fetch article detection: check if slug matches a cached article in Redux
    // This avoids the unnecessary 404 topic fetch when navigating from home/category pages
    // Also checks forYouSlice.feed.sectionData for articles cached by the For You feed
    const cachedArticleMatch = useSelector((state) => {
        if (!topicSlug || isSSRArticlePage) return null;

        // Check unifiedArticles scope cache
        const byScope = state.unifiedArticles?.articlesByScope || {};
        for (const scopeKey of Object.keys(byScope)) {
            const scopeArticles = byScope[scopeKey];
            if (Array.isArray(scopeArticles)) {
                for (const group of scopeArticles) {
                    if (group?.articles) {
                        const found = group.articles.find(a => a.slug === topicSlug);
                        if (found) return found;
                    }
                }
            }
        }

        // Check landing page article cache
        const landingArticles = state.landing?.articles;
        if (Array.isArray(landingArticles)) {
            for (const group of landingArticles) {
                if (group?.articles) {
                    const found = group.articles.find(a => a.slug === topicSlug);
                    if (found) return found;
                }
            }
        }

        // Check forYou feed section cache
        const sectionData = state.forYou?.feed?.sectionData;
        if (sectionData) {
            for (const sectionId of Object.keys(sectionData)) {
                const section = sectionData[sectionId];
                if (Array.isArray(section?.articles)) {
                    const found = section.articles.find(a => a.slug === topicSlug);
                    if (found) return found;
                }
            }
        }

        return null;
    });

    // Client-side article detection state (for when navigating via client-side link)
    // Initialize from cache match to prevent topic fetch on first render
    const [isArticleDetected, setIsArticleDetected] = useState(!!cachedArticleMatch || isArticleHint);
    const [isDetectingArticle, setIsDetectingArticle] = useState(false);

    // On URL change: set cached match if found, or clear stale article
    // Skip if SSR already set the correct article (don't wipe preloaded state on hydration)
    useEffect(() => {
        if (currentArticle?.slug === topicSlug) {
            // SSR or previous effect already set the correct article — don't clear it
            setIsArticleDetected(true);
            return;
        }
        dispatch(setCurrentArticle(cachedArticleMatch || null));
        setIsArticleDetected(!!cachedArticleMatch);
        setIsDetectingArticle(false);
    }, [topicSlug, dispatch]);

    // On SSR, useParams() returns undefined (no router context).
    // Get currentScope from Redux (set by SSR controller) as fallback.
    const ssrScope = useSelector((state) => state.unifiedArticles?.currentScope);
    // Read the current page from Redux (SSR-safe: set by SSR controller in preloadedState)
    const reduxPage = useSelector(state => state.unifiedArticles?.pagination?.page || 1);

    // Build scope from route params on client, use SSR scope as fallback
    const scope = useMemo(() => {
        let baseScope;
        if (topicSlug) {
            baseScope = buildScopeFromParams(categorySlug, topicSegments);
        } else if (ssrScope && ssrScope.type === 'topic') {
            baseScope = ssrScope;
        } else {
            baseScope = buildScopeFromParams(categorySlug, topicSegments);
        }
        return reduxPage > 1 ? { ...baseScope, page: reduxPage } : baseScope;
    }, [categorySlug, topicSlug, ssrScope, reduxPage]);

    // Use the unified article scope hook - handles caching, SSR hydration, and fetching
    // Skip fetching topic articles if this is actually an article page
    const {
        articles: articlesToDisplay,
        isLoading,
        error,
        pagination,
        topicMetadata,
        scopeKey,
        refetch,
    } = useArticleScope(scope, { skipFetch: isSSRArticlePage || isArticleDetected || isArticleHint });

    // Get topic data from preloaded state (SSR) or from unified slice
    const currentTopicFromRedux = useSelector((state) => state.currentTopic);
    const currentTopic = topicMetadata || currentTopicFromRedux;

    // Reactions state
    const {
        byArticleId: reactionsByArticleId,
        loading: reactionsLoading,
        fetched: reactionsFetched,
    } = useSelector(
        (state) =>
            state.reactions || {
                byArticleId: {},
                loading: { bulk: false },
                fetched: {},
            }
    );

    // Current page from pagination state
    const currentPage = pagination?.page || 1;

    // Extract all article IDs for bulk reaction loading
    const allArticleIds = useMemo(() => {
        if (!Array.isArray(articlesToDisplay)) return [];

        const ids = articlesToDisplay.flatMap((group) =>
            group && Array.isArray(group.articles)
                ? group.articles.map((article) => article?._id).filter(Boolean)
                : []
        );
        return [...new Set(ids)];
    }, [articlesToDisplay]);

    // Determine which article IDs need reactions fetched
    const unfetchedReactionIds = useMemo(() => {
        return allArticleIds.filter(
            (articleId) => !reactionsFetched[articleId] && !reactionsByArticleId[articleId]
        );
    }, [allArticleIds, reactionsFetched, reactionsByArticleId]);

    // Handle page change - update URL (enables browser history)
    const handlePageChange = useCallback((newPage, updateUrl = true) => {
        if (updateUrl) {
            setSearchParams({ page: newPage.toString() });
        }
        // Update pagination — scope change via URL triggers auto-fetch in useArticleScope
        dispatch(setPagination({ page: newPage }));
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [setSearchParams, dispatch]);

    // Client-side article detection: when topic fetch fails with error, try loading as article
    useEffect(() => {
        if (!error || isSSRArticlePage || isArticleDetected || isDetectingArticle || !topicSlug) {
            return;
        }

        const tryLoadArticle = async () => {
            setIsDetectingArticle(true);
            try {
                // Try streamlined article first
                const response = await axios.get(`/q/api/streamlined-articles/by-slug/${topicSlug}`);
                if (response.data?.data) {
                    dispatch(setCurrentArticle(response.data.data));
                    setIsArticleDetected(true);
                    setIsDetectingArticle(false);
                    return;
                }
            } catch (e) {
                // Not a streamlined article, try generated
            }
            try {
                const response = await axios.get(`/q/api/article-api/by-slug/${topicSlug}`);
                if (response.data) {
                    dispatch(setCurrentArticle(response.data));
                    setIsArticleDetected(true);
                    setIsDetectingArticle(false);
                    return;
                }
            } catch (e) {
                // Not a generated article either
            }
            setIsDetectingArticle(false);
        };

        tryLoadArticle();
    }, [error, topicSlug, isSSRArticlePage, isArticleDetected, isDetectingArticle, dispatch]);

    // Sync topic to currentTopic slice for compatibility.
    // NOTE: Do NOT include currentTopic in deps — this effect WRITES to
    // currentTopic (via setCurrentTopic), so depending on it creates a
    // dispatch→selector→effect feedback loop (infinite renders).
    useEffect(() => {
        if (topicSlug && !isSSRArticlePage && !isArticleDetected) {
            const ancestors = topicSegments.slice(0, -1);
            // Guard: only reuse currentTopic label if it's actually for THIS slug
            // (prevents stale label from a previous topic leaking into the new one)
            const safeTopicLabel = (currentTopic?.slug === topicSlug && currentTopic?.label) ? currentTopic.label : topicSlug;
            const safeCategoryLabel = pureCategories.find(c => c.name === categorySlug)?.label
                || (currentTopic?.category?.code === categorySlug ? currentTopic?.category?.label : null)
                || categorySlug;
            dispatch(setCurrentTopic({
                code: topicSlug,
                label: safeTopicLabel,
                slug: topicSlug,
                ancestors: ancestors.length ? ancestors.map(slug => ({ slug, label: null })) : [],
                category: {
                    code: categorySlug,
                    label: safeCategoryLabel,
                },
            }));
        }
    }, [dispatch, topicSlug, categorySlug, isSSRArticlePage, isArticleDetected]); // eslint-disable-line react-hooks/exhaustive-deps

    // On mount/topic change, set page metadata
    useEffect(() => {
        if (isSSRArticlePage || isArticleDetected) return; // Article page handles its own metadata
        const label = currentTopic?.label || topicSlug || 'Topic';
        const pageTitle = `${label} - Nexus of Truth`;
        const pageDesc = `Latest ${label} news and articles.`;
        dispatch(setPageTitle(pageTitle));
        dispatch(setPageDescription(pageDesc));
        const baseUrl = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '';
        const topicPath = `/${categorySlug}/${[...topicSegments].join('/')}`;
        const canonicalUrl = baseUrl + topicPath;
        dispatch(setCanonicalUrl(canonicalUrl));
        // Extract first available article image for social cards
        let firstImage = null;
        if (Array.isArray(articlesToDisplay)) {
            for (const group of articlesToDisplay) {
                if (group?.articles?.length) {
                    for (const a of group.articles) {
                        const img = a?.generatedImages?.[0]?.url || a?.images?.[0]?.url;
                        if (img) { firstImage = img; break; }
                    }
                }
                if (firstImage) break;
            }
        }
        dispatch(setPageMetadata({
            'og:title': pageTitle,
            'og:description': pageDesc,
            'og:type': 'website',
            'og:url': canonicalUrl,
            ...(firstImage ? { 'og:image': firstImage } : {}),
            'twitter:card': firstImage ? 'summary_large_image' : 'summary',
            'twitter:title': pageTitle,
            'twitter:description': pageDesc,
            ...(firstImage ? { 'twitter:image': firstImage } : {}),
        }));
        dispatch(
            setHeaderData({
                title: pageTitle,
                description: pageDesc,
            })
        );

        return () => {
            dispatch(resetPageMetadata());
        };
    }, [dispatch, currentTopic, topicSlug, isSSRArticlePage, isArticleDetected, articlesToDisplay]);

    // Smart bulk reactions fetching
    useEffect(() => {
        const shouldFetchReactions =
            unfetchedReactionIds.length > 0 && !reactionsLoading.bulk && !isLoading;

        if (shouldFetchReactions) {
            dispatch(fetchBulkReactions({ articleIds: unfetchedReactionIds }));
        }
    }, [unfetchedReactionIds, dispatch, reactionsLoading.bulk, isLoading]);

    // Rating aggregates — separate effect so reaction state changes don't re-trigger
    useEffect(() => {
        if (allArticleIds.length > 0 && !isLoading) {
            dispatch(fetchBulkRatingAggregates({ articleIds: allArticleIds }));
        }
    }, [allArticleIds, dispatch, isLoading]);

    // Sync URL page to Redux on mount and when URL changes (back/forward)
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    useEffect(() => {
        if (isMounted && urlPage !== currentPage) {
            dispatch(setPagination({ page: urlPage }));
        }
    }, [urlPage, isMounted]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── All hooks declared above ─── Early returns below ──

    // If SSR detected this as an article page OR client-side detection found an article
    // Guard: only render article detail once currentArticle actually matches the current URL
    // (prevents rendering with stale article from previous page before effect dispatches the update)
    if ((isSSRArticlePage || isArticleDetected) && currentArticle?.slug === topicSlug) {
        return <UnifiedArticleDetailPage />;
    }

    // Show loading while detecting if this is an article
    if (isDetectingArticle) {
        return (
            <main className="home-page-container topic-page">
                <div className="home-loading-container">
                    <div className="home-loading-spinner"></div>
                    <p>Loading...</p>
                </div>
            </main>
        );
    }

    // UI state calculations
    const showLoading = isLoading && articlesToDisplay.length === 0;
    const noArticlesAvailable = !showLoading && !error && articlesToDisplay.length === 0;
    const hasError = !!error;

    const topicLabel = currentTopic?.label || topicSlug || 'Topic';

    // Debug logging
    console.log('[TopicPage] Scope:', scopeKey, '| Articles:', articlesToDisplay.length, '| Loading:', isLoading);

    return (
        <main className="home-page-container topic-page">
            {/* Breadcrumb Navigation */}
            <Breadcrumbs />

            {showLoading ? (
                <div className="home-loading-container">
                    <div className="home-loading-spinner"></div>
                    <p>Loading {topicLabel} articles...</p>
                </div>
            ) : hasError ? (
                <div className="home-error-message">
                    <h2>Error loading articles</h2>
                    <p>{error?.toString() || 'An unknown error occurred'}</p>
                    <button onClick={() => refetch()}>Try Again</button>
                </div>
            ) : noArticlesAvailable ? (
                <div className="home-no-articles-message">
                    <h2>No {topicLabel} articles available</h2>
                    <p>Check back soon for the latest content.</p>
                </div>
            ) : (
                <HomePageContent
                    articlesGroupedByCategory={articlesToDisplay}
                    linkContext={{
                        categorySlug: categorySlug || ssrScope?.category,
                        topicSlug: topicSlug || ssrScope?.topic,
                    }}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                />
            )}
        </main>
    );
};

export default TopicPage;
